import { Redirect } from "@shopify/app-bridge/actions";
import { Formik } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { useShopifyAppBridge } from "src/components/shopify/appBridge";
import { ERROR_MESSAGES, SETTINGS_MESSAGES } from "src/constants/messages";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import Api from "src/data/api";
import { showNotification } from "src/redux/notificationActions";
import {
    validateEmailFields,
    validateEmailsFields,
    validateRequiredAddressesFields,
    validateRequiredFields,
    validateRequiredSingleAddressFields,
} from "src/utils/formValidations";
import { SETTINGS_GENERAL_FORM_INITIAL_VALUES } from "../RetailerSettingsConstants";

const updateRetailerSettings = async (data) => {
    let result = {};

    result = await Api.request({
        method: "POST",
        url: `/retailer/settings`,
        data,
    });

    return result;
};

const SettingsGeneralFormik = ({
    isShopify,
    generalSettings = {},
    children,
    setRefetch,
}) => {
    const dispatch = useDispatch();
    let getAppBridge;
    if (isShopify) {
        getAppBridge = useShopifyAppBridge().getAppBridge;
    }

    const currentSubscription = generalSettings.availableSubscriptions?.find(
        (subscription) => subscription.current
    )?.subscription_id;

    return (
        <Formik
            enableReinitialize
            initialValues={{
                ...SETTINGS_GENERAL_FORM_INITIAL_VALUES,
                ...generalSettings,
                subscription: currentSubscription,
            }}
            validate={(values) => {
                const requiredFields = ["name", "websiteUrl", "email"];
                const emailsFields = ["additionalEmails"];
                const emailFields = ["email"];

                const multipleAddressesFields = values.isRetailerOnlineOnly
                    ? []
                    : ["additionalAddresses"];

                const singleAddressFields = values.isRetailerOnlineOnly
                    ? []
                    : ["address"];

                return {
                    ...validateRequiredFields(values, requiredFields),
                    ...validateEmailsFields(values, emailsFields),
                    ...validateEmailFields(values, emailFields),
                    ...validateRequiredSingleAddressFields(
                        values,
                        singleAddressFields
                    ),
                    ...validateRequiredAddressesFields(
                        values,
                        multipleAddressesFields
                    ),
                };
            }}
            onSubmit={async (values, { setSubmitting }) => {
                const { address, additionalAddresses, ...rest } = values;
                const addresses = [address, ...additionalAddresses].filter(
                    (addr) => addr.neighborhood && addr.address
                );

                const data = {
                    ...rest,
                    additionalEmails: values.additionalEmails.filter(
                        (email) => email
                    ),
                    addresses,
                };

                try {
                    const result = await updateRetailerSettings(data);

                    if (!result?.data?.invalidAddresses.length) {
                        dispatch(
                            showNotification(
                                SETTINGS_MESSAGES.UPDATE_RETAILER_SETTINGS_SUCCESS,
                                NOTIFICATION_TYPES.SUCCESS,
                                NOTIFICATION_HEADERS.RETAILER_SETTINGS
                            )
                        );
                    }

                    if (result?.data?.invalidAddresses.length) {
                        const message = result?.data?.invalidAddresses.map(
                            (address) =>
                                `${address.address} is not a valid address. \n`
                        );
                        dispatch(
                            showNotification(
                                message,
                                NOTIFICATION_TYPES.ERROR,
                                NOTIFICATION_HEADERS.OOPS
                            )
                        );
                    }

                    if (!!result.data.redirect_url) {
                        const app = getAppBridge();
                        Redirect.create(app).dispatch(
                            Redirect.Action.REMOTE,
                            result.data.redirect_url
                        );
                    }
                } catch (err) {
                    console.log(err);
                    dispatch(
                        showNotification(
                            ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                            NOTIFICATION_TYPES.ERROR,
                            NOTIFICATION_HEADERS.OOPS
                        )
                    );
                } finally {
                    setRefetch((x) => !x);
                    setSubmitting(false);
                }
            }}
        >
            {children}
        </Formik>
    );
};

export default SettingsGeneralFormik;
